import React, { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';

import { useNotification } from 'config/Notification';

import { fetchPPTReports as fetchPPTReportsService, updatePPTReports as updatePPTReportsService } from 'services/modules/ecs/report';

import UploadIcon from '@mui/icons-material/Upload';

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  CircularProgress,
  TextField,
  Card,
  CardHeader,
  Divider,
  CardContent
} from '@mui/material';

const UploadPPTReportPageECS = () => {
  // State Variables
  const [fileError, setFileError] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFile, setSelectedFile] = useState({});
  const [totalResults, setTotalResults] = useState(0);
  const [userData, setUserData] = useState(null);

  // Notification
  const { openNotification } = useNotification();

  useEffect(() => {
    fetchCompanyReportsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchQuery]);

  // Method to fetch the report data of the assessment completed companies 
  const fetchCompanyReportsData = async () => {
    try {
      setLoading(true);
      const params = { page: page + 1, limit: rowsPerPage, sortBy: '-createdAt', reportStatus: 'COMPLETED' };
      if (searchQuery.trim() !== '') {
        params.search = searchQuery.trim();
      }
      const response = await fetchPPTReportsService({ params });
      setTotalResults(response?.data.totalResults);
      setUserData(response.data.results);
      setLoading(false);
    }
    catch (error) {
      openNotification('error while fetching PPT report data', 'error');
      setLoading(false);
    }
  };

  // This method handles to change the pages
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // This method handles to change the rows per page based on user selection
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Method to change the file before uploading

  const handleFileChange = (event, rowId) => {
    const file = event.target.files[0];

    if (file) {
      const validExtensions = [".pdf"];
      const fileExtension = file.name.slice(((file.name.lastIndexOf(".") - 1) >>> 0) + 2).toLowerCase();

      if (validExtensions.includes(`.${fileExtension}`)) {
        if (file.size <= 6 * 1024 * 1024) {
          setSelectedFile((prevFiles) => ({
            ...prevFiles,
            [rowId]: file,
          }));
          setFileError((prevErrors) => ({
            ...prevErrors,
            [rowId]: null,
          }));
        } else {
          setSelectedFile((prevFiles) => ({
            ...prevFiles,
            [rowId]: null,
          }));
          setFileError((prevErrors) => ({
            ...prevErrors,
            [rowId]: "File size exceeds the maximum limit of 6MB",
          }));
        }
      } else {
        setSelectedFile((prevFiles) => ({
          ...prevFiles,
          [rowId]: null,
        }));
        setFileError((prevErrors) => ({
          ...prevErrors,
          [rowId]: "Please choose a valid PDF file",
        }));
      }
    }
  };


  // This method handles to submit the file
  const handleFileSubmit = async (reportId) => {
    if (selectedFile) {
      try {
        setLoading(true);
        const formData = new FormData();
        const file = Object.values(selectedFile)[0];
        formData.append('data', file);
        await updatePPTReportsService({ reportId, formData })
        openNotification('File uploaded sucessfully', 'success')
        fetchCompanyReportsData();
      }
      catch (error) {
        openNotification('Error submitting file:', 'error')
      }
      finally {
        setLoading(false);
      }
    }
    else {
      openNotification('Invalid file selected.', 'error')
    }
  };

  // Method displays the formated date of report
  const formatDate = (date) => {
    const formattedDate = new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
    return formattedDate;
  };

  // Method displays the formated time of report
  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString();
  };

  return (
    <div className='card-container'>
      <Card className='card card-width' >
        <CardHeader
          title={
            <React.Fragment>
              <div className='card-head'>
                <label className='label'> Upload ppt reports</label>
              </div>
            </React.Fragment>
          }
        />
        <Divider className='divider' />
        <div className='flex-card'>
          <div className='m-2'>
            <TextField
              label="Search"
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead className="table-head">
                <TableRow>
                  <TableCell>Report id</TableCell>
                  <TableCell>Domain URL</TableCell>
                  <TableCell>Requested on</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Upload</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : (
                  (userData.length === 0) ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No data available.
                      </TableCell>
                    </TableRow>
                  ) : (
                    userData.map((report) => (
                      <TableRow key={report.reportId}>
                        <TableCell className="name-table">{report.reportId}</TableCell>
                        <TableCell className="text-secondary">{report.domain}</TableCell>
                        <TableCell>
                          <div className='report-datetime'>
                            <p className="text-bold text-black m-0" >{formatDate(report.createdAt)}</p>
                            <p className="text-primary subDim-text m-0" >{formatTime(report.createdAt)}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-bold text-black m-0">{report.reportStatus}</p>
                        </TableCell>
                        <TableCell>
                          <Form.Group>


                            <input type="file"
                              accept=".pdf"
                              onChange={(e) => handleFileChange(e, report.reportId)}
                              className={`form-control-file ${fileError[report.reportId] ? "is-invalid" : ""}`}
                            />
                            {fileError[report.reportId] && <div className="invalid-feedback">{fileError[report.reportId]}</div>}

                          </Form.Group>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="primary"
                            onClick={() => handleFileSubmit(report.reportId)}
                          >
                            <UploadIcon /> Submit file
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalResults}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default UploadPPTReportPageECS;

